/**
 * PropertyAdminInfoTab
 * --------------------
 * Property-admin tab where an agent can see order items linked to one of
 * their properties, unassociate items, and associate loose items placed
 * from their property pages.
 *
 * Behaviour rules (from user brief):
 *   - If the agent has exactly ONE linked property, hide the selector and
 *     auto-associate every eligible unassociated item to that property.
 *   - Otherwise, show a property dropdown of the agent's own properties.
 *   - Payment-status filter: All | Paid.
 *   - Items are grouped by product category in both lists.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Info as InfoIcon } from "lucide-react";
import { resolveImageUrl } from "@/utils/imageUrl";

const API = getApiUrl();

function currencySymbol(code) {
  return ({ GBP: "£", USD: "$", EUR: "€" }[code] || "");
}

function groupByProperty(items, properties) {
  const titleFor = (pid) => {
    if (!pid) return "Unassigned";
    const p = properties.find((x) => x.property_id === pid);
    return p ? (p.title || p.display_address || p.property_id) : pid;
  };
  const map = new Map();
  for (const it of items) {
    // For unassociated items we bucket by the order's `context_property_id`
    // (the property page the visitor was on when they checked out); for
    // associated items we bucket by their assigned `property_id`.
    const pid = it.property_id || it.context_property_id || "";
    if (!map.has(pid)) map.set(pid, { key: pid, label: titleFor(pid), items: [] });
    map.get(pid).items.push(it);
  }
  return Array.from(map.values()).sort((a, b) => {
    if (a.label === "Unassigned") return 1;
    if (b.label === "Unassigned") return -1;
    return a.label.localeCompare(b.label);
  });
}

function groupByCategory(items) {
  const map = new Map();
  for (const it of items) {
    const key = it.category_id || "";
    const label = it.category_name || "Uncategorised";
    if (!map.has(key)) map.set(key, { key, label, items: [] });
    map.get(key).items.push(it);
  }
  // Alphabetical by label, "Uncategorised" last.
  return Array.from(map.values()).sort((a, b) => {
    if (a.label === "Uncategorised") return 1;
    if (b.label === "Uncategorised") return -1;
    return a.label.localeCompare(b.label);
  });
}

function ItemRow({ item, testIdPrefix }) {
  const isAssociated = !!item.is_associated;
  const status = item.payment_status || item.status || "pending";
  const badgeCls =
    status === "paid"
      ? "bg-green-100 text-green-700"
      : status === "failed"
      ? "bg-red-100 text-red-700"
      : "bg-amber-100 text-amber-700";
  const img = item.image_url ? resolveImageUrl(item.image_url) : null;
  return (
    <div
      className="flex items-center gap-3 border-t border-border p-3"
      data-testid={`${testIdPrefix}-row-${item.order_id}-${item.item_id}`}
    >
      <div className="w-14 h-14 bg-muted flex-shrink-0 overflow-hidden">
        {img ? (
          <img
            src={img}
            alt={item.name}
            className="w-full h-full object-cover"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        ) : null}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate flex items-center gap-2">
              {item.name}
              {isAssociated && (
                <span
                  className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/30"
                  data-testid={`${testIdPrefix}-assoc-badge-${item.order_id}-${item.item_id}`}
                >
                  Linked
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {item.quantity} × {currencySymbol(item.currency)}
              {Number(item.price || 0).toFixed(2)} · Order #
              {(item.order_id || "").slice(0, 8).toUpperCase()}
            </div>
            {item.supplier_name && (
              <div
                className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2"
                data-testid={`${testIdPrefix}-supplier-${item.order_id}-${item.item_id}`}
              >
                <span>
                  Supplied by <span className="font-medium text-foreground">{item.supplier_name}</span>
                </span>
                {item.supplier_email && (
                  <a
                    href={`mailto:${item.supplier_email}`}
                    className="text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`${testIdPrefix}-supplier-email-${item.order_id}-${item.item_id}`}
                  >
                    {item.supplier_email}
                  </a>
                )}
                {item.supplier_phone && (
                  <a
                    href={`tel:${item.supplier_phone.replace(/[^+\d]/g, "")}`}
                    className="text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`${testIdPrefix}-supplier-phone-${item.order_id}-${item.item_id}`}
                  >
                    {item.supplier_phone}
                  </a>
                )}
              </div>
            )}
          </div>
          <span
            className={`text-[10px] px-2 py-0.5 rounded ${badgeCls} capitalize whitespace-nowrap`}
          >
            {status}
          </span>
        </div>
        <div className="text-[11px] text-muted-foreground mt-1 truncate">
          Placed {item.created_at ? new Date(item.created_at).toLocaleString() : "—"}
          {item.customer?.email ? ` · ${item.customer.email}` : ""}
        </div>
      </div>
    </div>
  );
}

function CategoryGroup({ group, testIdPrefix }) {
  return (
    <div className="border border-border mb-4" data-testid={`${testIdPrefix}-category-${group.key || "none"}`}>
      <div className="px-3 py-2 bg-muted/30 text-xs font-semibold uppercase tracking-wide flex items-center justify-between">
        <span>{group.label}</span>
        <span className="text-muted-foreground normal-case font-normal">
          {group.items.length} item{group.items.length === 1 ? "" : "s"}
        </span>
      </div>
      {group.items.map((it) => (
        <ItemRow
          key={`${it.order_id}-${it.item_id}`}
          item={it}
          testIdPrefix={testIdPrefix}
        />
      ))}
    </div>
  );
}

export default function PropertyAdminInfoTab({ linkedProperties, forcedPropertyId = null }) {
  const properties = linkedProperties || [];
  const singleProperty = properties.length === 1 ? properties[0] : null;

  // "all" (special) shows items across every linked property. When the agent
  // has just one property, or a card CTA forces a specific property, we
  // default to it and skip the selector.
  const [selectedPropertyId, setSelectedPropertyId] = useState(
    forcedPropertyId || singleProperty?.property_id || "all"
  );
  const [paymentStatus, setPaymentStatus] = useState("all"); // all | paid
  const [loading, setLoading] = useState(false);
  const [associated, setAssociated] = useState([]);
  const [unassociated, setUnassociated] = useState([]);

  // Keep selection valid if the property list changes. "all" is always valid.
  useEffect(() => {
    if (forcedPropertyId) {
      setSelectedPropertyId(forcedPropertyId);
      return;
    }
    if (selectedPropertyId === "all") return;
    if (!properties.find((p) => p.property_id === selectedPropertyId)) {
      setSelectedPropertyId(properties[0]?.property_id || "all");
    }
  }, [properties, selectedPropertyId, forcedPropertyId]);

  const load = useCallback(async () => {
    if (!selectedPropertyId) return;
    setLoading(true);
    try {
      const isAll = selectedPropertyId === "all";
      const suffix = isAll ? "" : `/${selectedPropertyId}`;
      const [a, u] = await Promise.all([
        axios.get(
          `${API}/property-admin/order-items/associated${suffix}?payment_status=${paymentStatus}`,
          { withCredentials: true }
        ),
        axios.get(
          `${API}/property-admin/order-items/unassociated${suffix}?payment_status=${paymentStatus}`,
          { withCredentials: true }
        ),
      ]);
      setAssociated(a.data?.items || []);
      setUnassociated(u.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load items");
    } finally {
      setLoading(false);
    }
  }, [selectedPropertyId, paymentStatus]);

  useEffect(() => {
    load();
  }, [load]);

  // Item-level associations still surface as "Linked" pills, but the
  // Associate/Unassociate CTAs have been removed — item ↔ property linking is
  // now managed centrally in Admin > Products > Orders.
  const propertyGroups = useMemo(() => {
    const merged = [
      ...associated.map((it) => ({ ...it, is_associated: true })),
      ...unassociated.map((it) => ({ ...it, is_associated: false })),
    ];
    if (selectedPropertyId === "all") {
      return groupByProperty(merged, properties).map((g) => ({
        ...g,
        categoryGroups: groupByCategory(g.items),
      }));
    }
    return [{
      key: selectedPropertyId,
      label: "",
      items: merged,
      categoryGroups: groupByCategory(merged),
    }];
  }, [associated, unassociated, selectedPropertyId, properties]);

  return (
    <div className="bg-white border border-border p-6" data-testid="info-tab-content">
      <div className="flex items-start justify-between gap-4 mb-6 pb-4 border-b border-border">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <InfoIcon className="h-5 w-5" /> Info
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage which order items belong to which property.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4 mb-6">
        {properties.length > 1 && !forcedPropertyId && (
          <div className="min-w-[260px]">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Property
            </Label>
            <Select
              value={selectedPropertyId}
              onValueChange={setSelectedPropertyId}
            >
              <SelectTrigger
                className="rounded-none mt-1"
                data-testid="info-property-select"
              >
                <SelectValue placeholder="Choose a property" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  value="all"
                  data-testid="info-property-option-all"
                >
                  All properties
                </SelectItem>
                {properties.map((p) => (
                  <SelectItem
                    key={p.property_id}
                    value={p.property_id}
                    data-testid={`info-property-option-${p.property_id}`}
                  >
                    {p.title || p.display_address || p.property_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {properties.length === 1 && (
          <div className="min-w-[260px] text-sm">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Property
            </Label>
            <div
              className="mt-1 px-3 py-2 border border-border bg-muted/20"
              data-testid="info-property-single"
            >
              {singleProperty.title || singleProperty.display_address || singleProperty.property_id}
            </div>
          </div>
        )}
        <div>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            Payment status
          </Label>
          <Select value={paymentStatus} onValueChange={setPaymentStatus}>
            <SelectTrigger
              className="rounded-none mt-1 min-w-[160px]"
              data-testid="info-payment-status-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="info-payment-status-all">All</SelectItem>
              <SelectItem value="pending" data-testid="info-payment-status-pending">Pending</SelectItem>
              <SelectItem value="paid" data-testid="info-payment-status-paid">Paid</SelectItem>
              <SelectItem value="failed" data-testid="info-payment-status-failed">Failed</SelectItem>
              <SelectItem value="refunded" data-testid="info-payment-status-refunded">Refunded</SelectItem>
              <SelectItem value="cancelled" data-testid="info-payment-status-cancelled">Cancelled</SelectItem>
              <SelectItem value="test" data-testid="info-payment-status-test">Test</SelectItem>
              <SelectItem value="void" data-testid="info-payment-status-void">Void</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          className="rounded-none"
          onClick={load}
          disabled={loading || !selectedPropertyId}
          data-testid="info-refresh-btn"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            "Refresh"
          )}
        </Button>
      </div>

      {!selectedPropertyId || (selectedPropertyId === "all" && properties.length === 0) ? (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="info-empty-no-property">
          You have no properties yet. Create a property to start associating order items.
        </div>
      ) : (
        <section data-testid="info-items-section">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-serif text-lg font-medium">
              {selectedPropertyId === "all"
                ? "Order items across all your properties"
                : "Order items for this property"}
            </h3>
            <span className="text-xs text-muted-foreground">
              {associated.length} linked · {unassociated.length} unlinked
            </span>
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : propertyGroups.every((g) => g.items.length === 0) ? (
            <div
              className="border border-dashed border-border p-6 text-sm text-muted-foreground text-center"
              data-testid="info-items-empty"
            >
              {selectedPropertyId === "all"
                ? "No order items yet across any of your properties."
                : "No order items yet for this property."}
            </div>
          ) : (
            <div data-testid="info-items-list">
              {propertyGroups.map((pg) => (
                <div key={pg.key || "none"} className="mb-6">
                  {selectedPropertyId === "all" && (
                    <div
                      className="px-3 py-2 bg-primary/5 border-l-4 border-primary text-sm font-semibold flex items-center justify-between"
                      data-testid={`info-items-property-${pg.key || "none"}`}
                    >
                      <span>{pg.label}</span>
                      <span className="text-muted-foreground font-normal text-xs">
                        {pg.items.length} item{pg.items.length === 1 ? "" : "s"}
                      </span>
                    </div>
                  )}
                  {pg.categoryGroups.map((g) => (
                    <CategoryGroup
                      key={`${pg.key}-${g.key || "none"}`}
                      group={g}
                      testIdPrefix="info-items"
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
